import { useEffect, useState } from 'react'
import { ConfluencePage } from '../types'
import { getConfluencePage } from '../services/confluenceService'
import axios from 'axios'

export interface ConfluencePageState {
  page: ConfluencePage | undefined
  error: unknown | undefined
  isLoading: boolean
  isError: boolean
  loadPage: (id: string, signal?: AbortSignal) => Promise<void>
}

export const useConfluencePage = (id?: string): ConfluencePageState => {
  /**
   * The page.
   */
  const [page, setPage] = useState<ConfluencePage>()

  /**
   * The error.
   */
  const [error, setError] = useState<unknown>()

  /**
   * Indicates if the page is being loaded.
   */
  const [isLoading, setIsLoading] = useState<boolean>(true)

  /**
   * Indicates if there was an error.
   */
  const isError = error !== undefined

  /**
   * Load the page by ID.
   */
  const loadPage = async (id: string, signal?: AbortSignal): Promise<void> => {
    try {
      setIsLoading(true)
      setError(undefined)

      setPage(
        await getConfluencePage(id, {
          params: {
            'body-format': 'view'
          },
          signal
        })
      )
    } catch (error) {
      if (!axios.isCancel(error)) {
        setError(error)
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      const controller = new AbortController()
      loadPage(id, controller.signal)
      return () => controller.abort()
    }
  }, [id])

  return {
    page,
    error,
    isLoading,
    isError,
    loadPage
  }
}
